import { AxiosInstance, AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse, Method } from 'axios';
import getHttpService, { IHttpService } from './httpService';
import { sessionStorage } from './storage';

interface IApiEndpointConfig {
  id: string;
  url: string;
}

interface IAPIConfig {
  appVersion: string;
  api: IApiEndpointConfig[];
}

export interface IUrlParamType {
  [index: string]: string | number | boolean;
}

export interface IBaseApiOptions {
  data?: any;
  headers?: AxiosRequestHeaders;
  method?: Method;
  params?: any;
  urlParams?: IUrlParamType;
  url?: string;
  withCredentials?: boolean;
  token?: string;
  timeout?: number;
}

type Config = Map<string, string>;

export interface IBaseApiService {
  action<T>(action: string, options: IBaseApiOptions): Promise<AxiosResponse<T>>;
  setToken(token: string): void;
  setPrivateConfig(config: Config): void;
}

export const API_CONFIG_STORAGE_KEY = 'apiConfig';
let configUrl: string;
let config: Config;
let http: IHttpService;
let service: IBaseApiService;
let privateConfig: Config;

function parseApiConfig(api: IApiEndpointConfig[]): Config {
  const res = new Map();
  return api.reduce((result: Config, endpoint: IApiEndpointConfig) => {
    result.set(endpoint.id, endpoint.url);
    return result;
  }, res);
}

function setConfigUrl(url: string) {
  configUrl = url;
}

function setPrivateConfig(customConfig: Config) {
  privateConfig = customConfig;
}

/**
 * A method to get actual API config from session storage or from backend
 */
async function getConfig() {
  // return config promise if already set
  if (!config) {
    // check for config in session storage
    const configData: IAPIConfig | null = sessionStorage.load(API_CONFIG_STORAGE_KEY);

    // check for appVersion of config if found
    if (configData) {
      // if versions are equal - return saved config
      config = parseApiConfig(configData.api);
    } else {
      // get latest config from backend
      const response = await http.get<{ config: IAPIConfig }>(configUrl);
      const responseData: IAPIConfig = response.data.config;
      sessionStorage.save(API_CONFIG_STORAGE_KEY, responseData);
      config = parseApiConfig(responseData.api);
    }
  }

  if (privateConfig) {
    config = new Map<string, string>([...config, ...privateConfig]);
  }
  return Promise.resolve(config);
}

/**
 * retrieves an url for given action from API config
 * @param action action for which we want to get an url
 */
async function getEndpointURL(action: string): Promise<string | null> {
  const config = await getConfig();
  // get proper url from config map
  const url = config.get(action);
  if (!url) {
    // if url not found - throw error
    throw new Error(`API action '${action}' does not exist in the current configuration!`);
  }
  return url;
}

/**
 * Method parses given url and options and creates a proper axios request configuration
 * @param url request url template string received from API config
 * @param options additional options to modify request config
 */
function prepareRequestConfig(url: string, options: IBaseApiOptions) {
  const { data, headers, method, params, withCredentials, timeout } = options;
  const requestConfig: AxiosRequestConfig = {
    data,
    headers,
    method,
    params,
    url,
    withCredentials,
    timeout,
  };
  // parse url params
  if (options.urlParams && typeof options.urlParams !== 'undefined') {
    const params = options.urlParams;
    requestConfig.url = url.replace(/\/\{(\w+)\}/g, (_match, param) => {
      const value = params[param];
      return value ? `/${value}` : '';
    });
  }
  // add authorization token if given
  if (options.token) {
    requestConfig.headers = requestConfig.headers || {};
    requestConfig.headers.Authorization = `Bearer ${options.token}`;
  } else {
    delete requestConfig.headers;
  }
  return requestConfig;
}

/**
 * Main method to make a request to NeuroNation API - to be reused in descendant classes
 * @param action - action to call on API - defines the actual url of the request
 * @param options - options to apply to request including urlParams, headers, token etc.
 */
async function action<T>(action: string, options: IBaseApiOptions) {
  const url = await getEndpointURL(action);
  if (!url) {
    throw new Error('Url is not defined for this action!');
  }
  const requestConfig = prepareRequestConfig(url, options);
  return http.request<T>(requestConfig);
}

/**
 * service constructor
 * @param configUrl url to the config endpoint of the NN API
 * @param instanceOverride instance of axios to override default
 */
function createService(configUrl: string, instanceOverride?: AxiosInstance, timeout?: number) {
  // we do not need to set baseUrl for HttpService
  // since every action will have a full url from the config
  http = getHttpService('', instanceOverride, timeout);
  setConfigUrl(configUrl);

  service = {
    action,
    setToken: http.setToken,
    setPrivateConfig,
  };
  return service;
}

/**
 * function returns existing singleton service instance or create
 * a new one if not created before
 * @param configUrl url to the config endpoint of the NN API
 * @param instanceOverride instance of axios to override default
 */
export function getBaseApiService(configUrl: string, instanceOverride?: AxiosInstance, timeout?: number) {
  if (service && !instanceOverride) {
    return service;
  }
  return createService(configUrl, instanceOverride, timeout);
}
export function resetBaseApiService() {
  //@ts-ignore
  service = undefined;
}

export default getBaseApiService;
